import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Button,
  Link as ChakraLink,
  Icon,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Image,
  AspectRatio,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import axios from 'axios';

const Leaderboards = ({ eventsData = [] }) => {
  const [standingsData, setStandingsData] = useState({});
  const [loadingStandings, setLoadingStandings] = useState({});
  const [errorStandings, setErrorStandings] = useState({});
  const [expandedEvents, setExpandedEvents] = useState({});

  // Filtra eventi che hanno un campo leaderboard non vuoto e ordina per data (più recenti prima)
  const eventsWithLeaderboards = eventsData
    .filter(
      (event) =>
        event.leaderboard &&
        event.leaderboard.trim() !== '' &&
        event.leaderboard.includes('spicerack.gg')
    )
    .sort((a, b) => {
      // Ordina per data decrescente (più recenti prima)
      const dateA = new Date(a.Data || a.data || 0);
      const dateB = new Date(b.Data || b.data || 0);
      return dateB - dateA;
    });

  // Funzione per estrarre l'ID dell'evento dall'URL di Spicerack
  const extractSpicerackEventId = (url) => {
    if (typeof url === 'string') {
      const match = url.match(/\/events\/(\d+)/);
      return match ? match[1] : null;
    }
    return null;
  };

  // Funzione per formattare la data
  const formatDate = (dateString) => {
    if (!dateString) return 'Data non disponibile';
    try {
      return new Date(dateString).toLocaleDateString('it-IT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'Data non valida';
    }
  };

  // Funzione per ottenere il titolo dell'evento
  const getEventTitle = (event) => {
    return event.Nome || event.titolo || event.title || 'Evento MTG';
  };

  // Funzione per ottenere la descrizione dell'evento
  const getEventDescription = (event) => {
    return event.Descrizione || event.descrizione || event.description || '';
  };

  // Funzione per ottenere l'URL dell'immagine dell'evento
  const getEventImageUrl = (event) => {
    if (event.thumbnail && event.thumbnail.url) {
      // Se l'URL inizia con /, aggiungi il base URL del server
      if (event.thumbnail.url.startsWith('/')) {
        return `${process.env.REACT_APP_API_URL || 'http://localhost:1337'}${event.thumbnail.url}`;
      }
      return event.thumbnail.url;
    }
    return null;
  };

  // Funzione per gestire l'espansione della classifica
  const toggleExpanded = (eventKey) => {
    setExpandedEvents((prev) => ({
      ...prev,
      [eventKey]: !prev[eventKey],
    }));
  };

  // Funzione per caricare i dati della leaderboard
  const loadStandings = async (eventId, event) => {
    const eventKey = event.id || event.documentId;

    setLoadingStandings((prev) => ({ ...prev, [eventKey]: true }));
    setErrorStandings((prev) => ({ ...prev, [eventKey]: null }));

    try {
      const response = await axios.get(
        `/api/spicerack/events/${eventId}/standings`
      );

      if (response.data.success) {
        setStandingsData((prev) => ({
          ...prev,
          [eventKey]: response.data.standings,
        }));
      } else {
        setErrorStandings((prev) => ({
          ...prev,
          [eventKey]: response.data.error || 'Errore nel caricamento dei dati',
        }));
      }
    } catch (error) {
      console.error('Error loading standings:', error);
      setErrorStandings((prev) => ({
        ...prev,
        [eventKey]:
          error.response?.data?.error ||
          'Errore nel caricamento della classifica',
      }));
    } finally {
      setLoadingStandings((prev) => ({ ...prev, [eventKey]: false }));
    }
  };

  // Carica automaticamente i dati della leaderboard quando la pagina si carica
  useEffect(() => {
    const loadAllStandings = async () => {
      const standingsPromises = eventsWithLeaderboards
        .map((event) => {
          const eventId = extractSpicerackEventId(event.leaderboard);
          return eventId ? loadStandings(eventId, event) : Promise.resolve();
        })
        .filter(Boolean);

      if (standingsPromises.length > 0) {
        await Promise.all(standingsPromises);
      }
    };

    if (eventsWithLeaderboards.length > 0) {
      loadAllStandings();
    }
  }, [eventsData]); // Si attiva quando cambiano i dati degli eventi

  // Statistiche calcolate
  const stats = {
    totalEvents: eventsWithLeaderboards.length,
    totalPlayers: Object.values(standingsData).reduce(
      (sum, standings) => sum + standings.length,
      0
    ),
    activeThisMonth: eventsWithLeaderboards.filter((event) => {
      if (!event.Data) return false;
      const eventDate = new Date(event.Data);
      const now = new Date();
      return (
        eventDate.getMonth() === now.getMonth() &&
        eventDate.getFullYear() === now.getFullYear()
      );
    }).length,
  };

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Box textAlign="center">
          <Heading as="h1" size="2xl" mb={4} color="brand.500">
            Leaderboards MTG
          </Heading>
          <Text fontSize="lg" color="gray.600">
            Classifiche dei tornei Magic: The Gathering
          </Text>
        </Box>

        {/* Statistiche generali */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          <Card>
            <CardBody textAlign="center">
              <Stat>
                <StatLabel>Eventi con Leaderboard</StatLabel>
                <StatNumber color="brand.500">{stats.totalEvents}</StatNumber>
                <StatHelpText>Totale eventi</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody textAlign="center">
              <Stat>
                <StatLabel>Giocatori Totali</StatLabel>
                <StatNumber color="brand.500">{stats.totalPlayers}</StatNumber>
                <StatHelpText>Partecipanti</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody textAlign="center">
              <Stat>
                <StatLabel>Eventi Questo Mese</StatLabel>
                <StatNumber color="brand.500">
                  {stats.activeThisMonth}
                </StatNumber>
                <StatHelpText>Gennaio 2025</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        <Divider />

        {/* Lista Eventi con Leaderboard */}
        {eventsWithLeaderboards.length === 0 ? (
          <Card>
            <CardBody textAlign="center" py={12}>
              <Heading as="h3" size="md" color="gray.500" mb={4}>
                Nessun evento con leaderboard disponibile
              </Heading>
              <Text color="gray.400">
                Gli eventi con classifiche appariranno qui una volta pubblicati.
              </Text>
            </CardBody>
          </Card>
        ) : (
          <>
            {/* Indicatore di caricamento globale */}
            {Object.values(loadingStandings).some((loading) => loading) && (
              <Card>
                <CardBody textAlign="center" py={8}>
                  <Spinner size="lg" color="brand.500" />
                  <Text mt={4}>Caricamento classifiche...</Text>
                </CardBody>
              </Card>
            )}
            <VStack spacing={6} align="stretch">
              {eventsWithLeaderboards.map((event, index) => {
                const eventId = extractSpicerackEventId(event.leaderboard);
                const leaderboardUrl = event.leaderboard;

                return (
                  <Card key={event.id || index} variant="outline">
                    <CardHeader>
                      <HStack spacing={4} align="start">
                        {/* Immagine dell'evento */}
                        {getEventImageUrl(event) && (
                          <Box flexShrink={0}>
                            <AspectRatio ratio={16 / 9} width="120px">
                              <Image
                                src={getEventImageUrl(event)}
                                alt={getEventTitle(event)}
                                objectFit="cover"
                                borderRadius="md"
                                fallback={
                                  <Box
                                    bg="gray.100"
                                    borderRadius="md"
                                    width="120px"
                                    height="68px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <Text fontSize="xs" color="gray.500">
                                      Immagine
                                    </Text>
                                  </Box>
                                }
                              />
                            </AspectRatio>
                          </Box>
                        )}

                        {/* Contenuto dell'evento */}
                        <VStack align="start" spacing={2} flex={1}>
                          <HStack
                            justify="space-between"
                            align="start"
                            width="100%"
                          >
                            <VStack align="start" spacing={2} flex={1}>
                              <Heading as="h3" size="lg" color="brand.500">
                                {getEventTitle(event)}
                              </Heading>
                              <HStack spacing={4}>
                                <Badge colorScheme="blue" fontSize="sm">
                                  {formatDate(event.Data)}
                                </Badge>
                                {eventId && (
                                  <Badge colorScheme="green" fontSize="sm">
                                    Evento #{eventId}
                                  </Badge>
                                )}
                              </HStack>
                              {getEventDescription(event) && (
                                <Text
                                  fontSize="sm"
                                  color="gray.600"
                                  noOfLines={2}
                                >
                                  {getEventDescription(event)}
                                </Text>
                              )}
                            </VStack>

                            {/* Pulsante per Spicerack */}
                            {leaderboardUrl && (
                              <Button
                                as={ChakraLink}
                                href={leaderboardUrl}
                                isExternal
                                size="sm"
                                colorScheme="blue"
                                variant="outline"
                                rightIcon={<Icon as={ExternalLinkIcon} />}
                              >
                                Vedi su Spicerack
                              </Button>
                            )}
                          </HStack>
                        </VStack>
                      </HStack>
                    </CardHeader>

                    {/* Leaderboard Data */}
                    <CardBody pt={0}>
                      {(() => {
                        const eventKey = event.id || event.documentId;
                        const standings = standingsData[eventKey];
                        const loading = loadingStandings[eventKey];
                        const error = errorStandings[eventKey];

                        if (loading) {
                          return (
                            <Box textAlign="center" py={4}>
                              <Spinner size="md" color="brand.500" />
                              <Text mt={2} fontSize="sm">
                                Caricamento classifica...
                              </Text>
                            </Box>
                          );
                        }

                        if (error) {
                          return (
                            <Alert status="error" mt={4} size="sm">
                              <AlertIcon />
                              {error}
                            </Alert>
                          );
                        }

                        if (standings && standings.length > 0) {
                          const isExpanded = expandedEvents[eventKey];
                          const displayStandings = isExpanded
                            ? standings
                            : standings.slice(0, 3);

                          return (
                            <Box mt={4}>
                              <HStack
                                justify="space-between"
                                align="center"
                                mb={4}
                              >
                                <Heading as="h4" size="md" color="brand.500">
                                  Classifica ({standings.length} giocatori)
                                </Heading>
                                {standings.length > 3 && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    colorScheme="blue"
                                    onClick={() => toggleExpanded(eventKey)}
                                  >
                                    {isExpanded
                                      ? 'Mostra meno'
                                      : `Mostra tutto (${standings.length - 3})`}
                                  </Button>
                                )}
                              </HStack>

                              <TableContainer>
                                <Table variant="simple" size="sm">
                                  <Thead>
                                    <Tr>
                                      <Th>Posizione</Th>
                                      <Th>Giocatore</Th>
                                      <Th isNumeric>Punti</Th>
                                      <Th isNumeric>Vittorie</Th>
                                      <Th isNumeric>Sconfitte</Th>
                                      <Th isNumeric>Pareggi</Th>
                                      <Th isNumeric>Win Rate</Th>
                                    </Tr>
                                  </Thead>
                                  <Tbody>
                                    {displayStandings.map(
                                      (player, playerIndex) => (
                                        <Tr
                                          key={`${eventKey}-player-${player.name || playerIndex}`}
                                        >
                                          <Td>
                                            <Badge
                                              colorScheme={
                                                player.rank === 1
                                                  ? 'yellow'
                                                  : player.rank === 2
                                                    ? 'gray'
                                                    : player.rank === 3
                                                      ? 'orange'
                                                      : 'blue'
                                              }
                                              variant="solid"
                                            >
                                              #{player.rank}
                                            </Badge>
                                          </Td>
                                          <Td fontWeight="medium">
                                            {player.name}
                                          </Td>
                                          <Td
                                            isNumeric
                                            fontWeight="bold"
                                            color="brand.500"
                                          >
                                            {player.points}
                                          </Td>
                                          <Td isNumeric color="green.500">
                                            {player.wins}
                                          </Td>
                                          <Td isNumeric color="red.500">
                                            {player.losses}
                                          </Td>
                                          <Td isNumeric color="blue.500">
                                            {player.draws || 0}
                                          </Td>
                                          <Td isNumeric>{player.winRate}%</Td>
                                        </Tr>
                                      )
                                    )}
                                  </Tbody>
                                </Table>
                              </TableContainer>

                              {!isExpanded && standings.length > 3 && (
                                <Text
                                  fontSize="sm"
                                  color="blue.500"
                                  textAlign="center"
                                  mt={2}
                                  cursor="pointer"
                                  textDecoration="underline"
                                  _hover={{ color: 'blue.700' }}
                                  onClick={() => toggleExpanded(eventKey)}
                                >
                                  ... e altri {standings.length - 3} giocatori
                                </Text>
                              )}
                            </Box>
                          );
                        }

                        return null; // Non mostrare nulla se non ci sono dati
                      })()}
                    </CardBody>
                  </Card>
                );
              })}
            </VStack>
          </>
        )}

        {/* Footer informativo */}
        <Box textAlign="center" py={4}>
          <Text fontSize="sm" color="gray.500">
            Le classifiche vengono aggiornate automaticamente dopo ogni torneo.
            <br />
            Per partecipare ai tornei, contatta l&apos;organizzazione CIL.
          </Text>
        </Box>
      </VStack>
    </Container>
  );
};

export default Leaderboards;
